import { useEffect, useMemo, useState } from "react";
import http from "@/api";
import { PILLARS, RECOGNITION_META, LEVEL_LABELS, toBase10 } from "@/constants";
import { EquivalenceTable } from "@/components/EquivalenceTable";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { AlertTriangle, Compass, TrendingUp } from "lucide-react";

const PILLAR_LABEL = Object.fromEntries(PILLARS.map((p) => [p.key, p.label]));

export default function RecognitionPanel({ supplierId }) {
  const [rec, setRec] = useState(null);
  const [hist, setHist] = useState(null);
  const [months, setMonths] = useState(6);
  const [pillarFilter, setPillarFilter] = useState("all");

  useEffect(() => {
    if (!supplierId) return;
    http.get(`/suppliers/${supplierId}/recognition`).then((r) => setRec(r.data)).catch(() => {});
    http.get(`/suppliers/${supplierId}/recognition-history?months=12`).then((r) => setHist(r.data)).catch(() => {});
  }, [supplierId]);

  const series = useMemo(() => {
    if (!hist?.series) return [];
    return hist.series.slice(-months).map((s) => ({
      period: s.period,
      score: toBase10(s.score),
      ...Object.fromEntries(Object.entries(s.pillars || {}).map(([k, v]) => [k, toBase10(v)])),
    }));
  }, [hist, months]);

  if (!rec) return null;
  const meta = RECOGNITION_META[rec.level] || {};
  const hasData = rec.average != null;

  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="recognition-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">reconocimiento LALA Elite · {rec.period}</div>
          <h3 className="font-display text-2xl">Calificación base 10</h3>
        </div>
        {hasData && rec.level && (
          <div className="text-right">
            <span className="sketch-pill px-4 py-1.5 text-sm font-bold text-white inline-flex items-center gap-1.5"
              style={{ background: meta.color }} data-testid="recognition-level-badge">
              {meta.icon} {rec.level_label || meta.label}
            </span>
            <div className="font-display text-3xl mt-1.5" style={{ color: meta.color }} data-testid="recognition-average">
              {toBase10(rec.average)?.toFixed(1)} <span className="text-sm text-[var(--ink-soft)] font-normal">/ 10</span>
            </div>
          </div>
        )}
      </div>

      {!hasData && (
        <div className="mt-4 text-sm text-[var(--ink-soft)]" data-testid="recognition-no-data">
          Aún no hay capturas de KPIs para calcular el reconocimiento de este periodo.
        </div>
      )}

      {rec.warning && (
        <div className="mt-4 p-4 rounded-xl border-2 border-[#E0162B] bg-[#FDEBEE] flex items-start gap-3"
          data-testid="development-warning">
          <AlertTriangle size={20} className="text-[#E0162B] shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-sm text-[#8F0E1D]">Requiere plan de desarrollo</div>
            <p className="text-sm text-[#8F0E1D] mt-0.5">{rec.warning}</p>
          </div>
        </div>
      )}

      {hasData && (
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-5">
          {PILLARS.map((p) => {
            const v = rec.pillar_scores?.[p.key];
            const w = rec.weights?.[p.key];
            if (v == null) return (
              <div key={p.key} className="flex justify-between text-sm text-[var(--ink-soft)]">
                <span>{p.label}{w != null && <span className="text-[11px]" data-testid={`pillar-weight-${p.key}`}> · pesa {Number(w).toFixed(0)}%</span>}</span><span>sin datos</span>
              </div>
            );
            return (
              <div key={p.key} data-testid={`recognition-pillar-${p.key}`}>
                <div className="flex justify-between text-sm font-semibold">
                  <span>{p.label}{w != null && <span className="text-[11px] text-[var(--ink-soft)] font-normal" data-testid={`pillar-weight-${p.key}`}> · pesa {Number(w).toFixed(0)}%</span>}</span>
                  <span className="text-[var(--lala-blue)]">{toBase10(v).toFixed(1)} <span className="text-xs text-[var(--ink-soft)] font-normal">· {LEVEL_LABELS[Math.max(1, Math.min(4, Math.round(v)))]}</span></span>
                </div>
                <div className="h-2.5 mt-1 rounded-full bg-[var(--cream-deep)]">
                  <div className="h-2.5 rounded-full" style={{ width: `${(v / 4) * 100}%`, background: meta.color || "var(--lala-blue)" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasData && rec.weights_source && (
        <p className="text-[11px] text-[var(--ink-soft)] mt-3" data-testid="weights-source-note">
          {rec.weights_source === "categoria"
            ? <>Calificación ponderada con los <strong>pesos de la categoría {rec.category}</strong> — cada categoría de compra valora sus pilares de forma distinta.</>
            : <>Calificación ponderada con los <strong>pesos globales del programa</strong>.</>}
        </p>
      )}

      {hasData && (
        <details className="mt-3" data-testid="equivalence-details">
          <summary className="text-xs text-[var(--lala-blue)] cursor-pointer font-semibold">¿Cómo se convierte la escala interna 1–4 a base 10?</summary>
          <div className="mt-2">
            <EquivalenceTable compact />
          </div>
        </details>
      )}

      {rec.improvement && (
        <div className="mt-5 p-4 rounded-xl bg-[var(--cream)] border border-[var(--line)]" data-testid="improvement-journey">
          <div className="font-hand text-lg text-[var(--lala-blue)] flex items-center gap-1.5">
            <Compass size={15} /> tu camino de mejora
          </div>
          <ul className="mt-2 space-y-1.5">
            {rec.improvement.messages.map((m, i) => (
              <li key={i} className="text-sm text-[var(--ink)] flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--lala-blue)] mt-1.5 shrink-0" />{m}
              </li>
            ))}
          </ul>
        </div>
      )}

      {series.length > 1 && (
        <div className="mt-6" data-testid="recognition-history">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="font-hand text-lg text-[var(--lala-blue)] flex items-center gap-1.5">
              <TrendingUp size={15} /> desempeño histórico
            </div>
            <div className="flex items-center gap-2">
              {[3, 6, 12].map((m) => (
                <button key={m} onClick={() => setMonths(m)} data-testid={`history-months-${m}`}
                  className={`sketch-pill px-3 py-1 text-xs font-semibold ${months === m ? "bg-[var(--lala-blue)] text-white" : "bg-[var(--cream)] text-[var(--lala-blue)]"}`}>
                  {m} meses
                </button>
              ))}
              <select value={pillarFilter} onChange={(e) => setPillarFilter(e.target.value)}
                className="input-field text-xs font-semibold py-1" data-testid="history-pillar-filter">
                <option value="all">Score global</option>
                {PILLARS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer width="100%" height={240} minWidth={200} minHeight={160}>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#D9CFB9" />
                <XAxis dataKey="period" stroke="#11142B" fontSize={11} />
                <YAxis domain={[2.5, 10]} ticks={[2.5, 5, 7.5, 10]} stroke="#11142B" fontSize={11} />
                <Tooltip contentStyle={{ background: "#F8F2E6", border: "1.5px solid #0033AD", borderRadius: 12 }} />
                {pillarFilter === "all" ? (
                  <Line type="monotone" dataKey="score" stroke="#0033AD" strokeWidth={3} dot={{ r: 4 }} name="Score global" connectNulls />
                ) : (
                  <Line type="monotone" dataKey={pillarFilter} stroke="#0033AD" strokeWidth={3} dot={{ r: 4 }}
                    name={PILLAR_LABEL[pillarFilter]} connectNulls />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-[var(--ink-soft)] mt-1">
            Los KPIs trimestrales/semestrales/anuales conservan su última calificación vigente hasta su próximo ciclo de evaluación.
          </p>
        </div>
      )}
    </div>
  );
}
